import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastProvider, useToast } from "../components/Toast";
import AdminSidebar from "../components/AdminSidebar";
import { createTestimonial } from "../../services/api";

const EMPTY_FORM = {
  name: "",
  role: "",
  company: "",
  avatar: "",
  avatarBg: "#6246ea",
  avatarCo: "#fff",
  course: "",
  text: "",
  rating: 5,
};

const AddTestimonialInner = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (
      !form.name.trim() ||
      !form.role.trim() ||
      !form.company.trim() ||
      !form.avatar.trim() ||
      !form.course.trim() ||
      !form.text.trim()
    ) {
      toast("Please fill all required fields", "error");
      return;
    }
    setSaving(true);
    try {
      await createTestimonial(form);
      toast("Testimonial added successfully!", "success");
      setTimeout(() => navigate("/admin/testimonials"), 1000);
    } catch (e) {
      toast(e.response?.data?.message || "Error adding testimonial", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar
        section="testimonials"
        onSectionChange={(id) => navigate("/admin", { state: { section: id } })}
      />
      <div className="admin-content">
        <div className="admin-section active">
          <div className="admin-header">
            <div className="admin-title">➕ Add New Testimonial</div>
            <button
              className="btn-secondary btn-sm"
              onClick={() => navigate("/admin/testimonials")}
            >
              ← Back to Testimonials
            </button>
          </div>

          <div className="admin-card" style={{ maxWidth: 2000 }}>
            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 20,
                color: "var(--accent)",
              }}
            >
              💬 Fill in the testimonial details
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Name *</label>
                <input
                  className="form-input"
                  placeholder="e.g. Priya Sharma"
                  value={form.name}
                  onChange={set("name")}
                />
              </div>
              <div>
                <label className="form-label">Role *</label>
                <input
                  className="form-input"
                  placeholder="e.g. Data Analyst"
                  value={form.role}
                  onChange={set("role")}
                />
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Company *</label>
                <input
                  className="form-input"
                  placeholder="e.g. Razorpay"
                  value={form.company}
                  onChange={set("company")}
                />
              </div>
              <div>
                <label className="form-label">Avatar Initials *</label>
                <input
                  className="form-input"
                  placeholder="e.g. PS"
                  maxLength={3}
                  value={form.avatar}
                  onChange={set("avatar")}
                />
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Avatar Background Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.avatarBg}
                    onChange={set("avatarBg")}
                    style={{
                      width: 40,
                      height: 36,
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 6,
                    }}
                  />
                  <input
                    className="form-input"
                    value={form.avatarBg}
                    onChange={set("avatarBg")}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Avatar Text Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.avatarCo}
                    onChange={set("avatarCo")}
                    style={{
                      width: 40,
                      height: 36,
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 6,
                    }}
                  />
                  <input
                    className="form-input"
                    value={form.avatarCo}
                    onChange={set("avatarCo")}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Course Name *</label>
              <input
                className="form-input"
                placeholder="e.g. Machine Learning Specialization"
                value={form.course}
                onChange={set("course")}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Testimonial Text *</label>
              <textarea
                className="form-input"
                rows={5}
                placeholder="What did the learner say..."
                value={form.text}
                onChange={set("text")}
                style={{ resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Rating (1–5)</label>
              <select
                className="form-input"
                value={form.rating}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    rating: Number(e.target.value),
                  }))
                }
                style={{ maxWidth: 160 }}
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n} ★
                  </option>
                ))}
              </select>
            </div>

            {/* Preview */}
            <div
              style={{
                background: "var(--bg2)",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 20,
                border: "1px solid var(--border)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text3)",
                  marginBottom: 10,
                  fontWeight: 600,
                }}
              >
                PREVIEW
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: form.avatarBg,
                    color: form.avatarCo,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {form.avatar || "?"}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {form.name || "Name"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>
                    {form.role || "Role"} @ {form.company || "Company"}
                  </div>
                </div>
                <div style={{ marginLeft: "auto", color: "#f59e0b" }}>
                  {"★".repeat(form.rating)}
                </div>
              </div>
              {form.text && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    color: "var(--text2)",
                    fontStyle: "italic",
                  }}
                >
                  "{form.text}"
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={saving}
                style={{ opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Saving..." : "💾 Save Testimonial"}
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate("/admin/testimonials")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddTestimonialPage = () => (
  <ToastProvider>
    <AddTestimonialInner />
  </ToastProvider>
);

export default AddTestimonialPage;
